import { useMemo, useState, useRef, useCallback, ChangeEvent } from 'react';

import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import {
  useRecoilValue,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';

import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';

import { Flexbox, Vertical } from 'src/components/Alignments';
import AddAnnotatorDialog from 'src/components/Modal/AddAnnotatorDialog';
import JobAssignDialog, {
  ASSIGNMENT_MODE,
} from 'src/components/Modal/JobAssignDialog';
import Table from 'src/components/Table';
import useDeferredState from 'src/hooks/useDeferredState';
import { jobState } from 'src/states/job';
import { projectState, useRefreshProject } from 'src/states/project';
import { useRefreshAnnotatorList, userState } from 'src/states/user';
import { Modality } from 'src/types/api/data/image';
import { JobsStatsState } from 'src/types/client/stats';
import { PathNames } from 'src/types/client/url';
import { Annotator, UserInfo } from 'src/types/client/user';
import { DEFAULT_PAGE_SIZE } from 'src/utils/constants';
import { UrlUtil } from 'src/utils/url';

const IconButtonGroup = styled('div')(
  ({ theme }) => `
  display: inline-flex;
  flex-direction: row;
  gap: ${theme.spacing(1)};
`
);

interface AnnotatorsProps {
  projectId: string;
  modality: Modality;
  jobStatsState?: JobsStatsState;
  navigateToJobsTab: () => void;
}

export default function Annotators({
  projectId,
  modality,
  jobStatsState,
  navigateToJobsTab,
}: AnnotatorsProps): JSX.Element {
  const navigate = useNavigate();

  const project = useRecoilValue(projectState.project({ projectId, modality }));
  const refreshProject = useRefreshProject();
  const refreshAnnotators = useRefreshAnnotatorList({
    projectId,
    modality,
  });

  const targetAnnotator = useRef<Annotator>();

  const setJobUserOption = useSetRecoilState(jobState.jobUserOption);

  const annotatorsLoadable = useRecoilValueLoadable(
    userState.annotatorList({ projectId, modality })
  );

  const annotatorList = useMemo(
    () =>
      annotatorsLoadable.state === 'hasValue'
        ? annotatorsLoadable.contents
        : [],
    [annotatorsLoadable.contents, annotatorsLoadable.state]
  );
  // Pagination
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(annotatorList.length / DEFAULT_PAGE_SIZE);

  // Controller dialogs
  const [openAddAnnotatorDialog, setOpenAddAnnotatorDialog] =
    useDeferredState(false);
  const [openJobAssignDialog, setOpenJobAssignDialog] = useDeferredState(false);
  const [openJobUnassignDialog, setOpenJobUnassignDialog] =
    useDeferredState(false);

  const handleClickJobAssignPlus = useCallback(
    (annotator: Annotator) => () => {
      targetAnnotator.current = annotator;
      setOpenJobAssignDialog(true);
    },
    [setOpenJobAssignDialog]
  );

  const handleClickJobUnassignPlus = useCallback(
    (annotator: Annotator) => () => {
      targetAnnotator.current = annotator;
      setOpenJobUnassignDialog(true);
    },
    [setOpenJobUnassignDialog]
  );

  const handleClickAddAnnotators = () => {
    setOpenAddAnnotatorDialog(true);
  };

  const handleCloseAddAnnotators = () => {
    setOpenAddAnnotatorDialog(false);
  };

  const handleCloseJobAssignDialog = () => {
    setOpenJobAssignDialog(false);
  };

  const handleCloseJobUnassignDialog = () => {
    setOpenJobUnassignDialog(false);
  };

  const handleUpdateJobAssign = () => {
    refreshProject();
    refreshAnnotators();
    setOpenJobAssignDialog(false);
  };

  const handleUpdateJobUnassign = () => {
    refreshProject();
    refreshAnnotators();
    setOpenJobUnassignDialog(false);
  };

  const handleAddAnnotator = () => {
    refreshAnnotators();
    setOpenAddAnnotatorDialog(false);
  };

  const handleClickViewJob = useCallback(
    (selectedUser: UserInfo) => () => {
      setJobUserOption({
        value: selectedUser.userId,
        label: selectedUser.userName,
      });
      navigate(
        UrlUtil.getUrl(PathNames.PROJECT_TAB, {
          modalityLabel: modality,
          projectId,
          tabName: 'jobs',
        })
      );
      navigateToJobsTab();
    },
    [modality, navigate, navigateToJobsTab, projectId, setJobUserOption]
  );

  const getUnassignableCount = useCallback(
    (annotator?: Annotator) =>
      (annotator?.jobStat.assignedStage ?? 0) +
      (annotator?.jobStat.queuedStage ?? 0),
    []
  );

  // Table elements
  const annotatorsColumnHeader = useMemo(() => {
    return [
      {
        Header: 'Annotator List',
        columns: [
          {
            Header: 'Annotator Username',
            accessor: 'username',
          },
          {
            Header: 'Annotation Progress (Done / Total)',
            id: 'progress',
            accessor: ({ jobStat: { completedStage, total } }: Annotator) =>
              `${completedStage} / ${total}`,
            style: { textAlign: 'center' },
          },
          {
            Header: 'Corroboration Count',
            id: 'corroborationCount',
            accessor: ({ corroborationStat: { completedStage } }: Annotator) =>
              completedStage,
            style: { width: '20%', textAlign: 'center' },
          },
          {
            Header: 'Assignment',
            id: 'targetUser',
            style: { width: '25%', textAlign: 'center' },
            Cell: (cell: CellProps<Annotator, Annotator>) => {
              return (
                <IconButtonGroup>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={handleClickJobAssignPlus(cell.row.original)}
                    disabled={
                      project.confirmed || jobStatsState === 'noJobLeft'
                    }
                  >
                    Assign
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={handleClickJobUnassignPlus(cell.row.original)}
                    disabled={
                      project.confirmed ||
                      getUnassignableCount(cell.row.original) === 0
                    }
                  >
                    Unassign
                  </Button>
                </IconButtonGroup>
              );
            },
          },
          {
            Header: 'Jobs',
            id: 'jobs',
            style: { width: '10%', textAlign: 'center' },
            Cell: function renderJobs(cell: CellProps<Annotator>) {
              return (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleClickViewJob({
                    userId: cell.row.original.id,
                    userName: cell.row.original.username,
                  })}
                >
                  View
                </Button>
              );
            },
          },
        ],
      },
    ];
  }, [
    handleClickJobAssignPlus,
    handleClickJobUnassignPlus,
    handleClickViewJob,
    getUnassignableCount,
    jobStatsState,
    project.confirmed,
  ]);

  const paginatedAnnotators: Annotator[] = useMemo(
    () =>
      annotatorList.slice(
        (page - 1) * DEFAULT_PAGE_SIZE,
        page * DEFAULT_PAGE_SIZE
      ),

    [annotatorList, page]
  );

  const handleChangePage = (event: ChangeEvent<unknown>, page: number) =>
    setPage(page);

  return (
    <Vertical>
      <Flexbox>
        <Button
          color="primary"
          variant="contained"
          onClick={handleClickAddAnnotators}
          disabled={project.confirmed}
          startIcon={<AddIcon />}
          style={{ width: 150 }}
        >
          Annotators
        </Button>
      </Flexbox>
      <Table<Annotator>
        columns={annotatorsColumnHeader}
        data={paginatedAnnotators}
        isLoading={annotatorsLoadable.state === 'loading'}
        isError={annotatorsLoadable.state === 'hasError'}
        onRefresh={refreshAnnotators}
        paginationProps={{
          page,
          count: totalPages,
          onChange: handleChangePage,
        }}
      />
      <AddAnnotatorDialog
        open={openAddAnnotatorDialog}
        onUpdate={handleAddAnnotator}
        onClose={handleCloseAddAnnotators}
        projectId={projectId}
      />
      <JobAssignDialog
        mode={ASSIGNMENT_MODE.ASSIGN}
        open={openJobAssignDialog}
        annotatorName={targetAnnotator.current?.username}
        annotatorId={targetAnnotator.current?.id}
        projectId={projectId}
        assignableJobCount={project.jobStats.annotation.createdStage}
        onClose={handleCloseJobAssignDialog}
        onUpdate={handleUpdateJobAssign}
      />
      <JobAssignDialog
        mode={ASSIGNMENT_MODE.UNASSIGN}
        open={openJobUnassignDialog}
        annotatorName={targetAnnotator.current?.username}
        annotatorId={targetAnnotator.current?.id}
        projectId={projectId}
        assignableJobCount={getUnassignableCount(targetAnnotator.current)}
        onClose={handleCloseJobUnassignDialog}
        onUpdate={handleUpdateJobUnassign}
      />
    </Vertical>
  );
}
